import { useMemo } from "react";
import { Body } from "domains/3d/interfaces";
import { GetModelsByModelIdApiResponse } from "infra/api/generated/api";
import _ from "lodash";

export function useIsDisabled({
  model,
  body,
}: {
  model: GetModelsByModelIdApiResponse["model"] | undefined;
  body: Body | undefined;
}): { isDisabled: boolean; reasons: string[] } {
  return useMemo(() => {
    if (!body) {
      return {
        isDisabled: true,
        reasons: [],
      };
    }

    const disabledReasons = _.uniq(
      _.compact(
        model?.inputs?.map((input) => {
          if (!input.required) {
            return undefined;
          }

          const isValidAndDefined = getIsInputValidAndDefined(
            input,
            body[input.name]
          );

          const ifDefinedKeys = Object.keys(input.required.ifDefined ?? {});
          if (ifDefinedKeys.length) {
            for (const key of ifDefinedKeys) {
              const linkedInput = model?.inputs?.find((f) => f.name === key);
              if (
                linkedInput &&
                getIsInputValidAndDefined(linkedInput, body[key]) &&
                !isValidAndDefined
              ) {
                return (
                  input.required.ifDefined as
                    | Record<string, { message: string }>
                    | undefined
                )?.[key]?.message;
              }
            }
          }

          const ifNotDefined = Object.keys(input.required.ifNotDefined ?? {});
          if (ifNotDefined.length) {
            for (const key of ifNotDefined) {
              const linkedInput = model?.inputs?.find((f) => f.name === key);
              if (
                linkedInput &&
                !getIsInputValidAndDefined(linkedInput, body[key]) &&
                !isValidAndDefined
              ) {
                return (
                  input.required.ifNotDefined as
                    | Record<string, { message: string }>
                    | undefined
                )?.[key]?.message;
              }
            }
          }

          if (input.required.always && !isValidAndDefined) {
            return `${input.label ?? input.name} is required`;
          }

          return undefined;
        })
      )
    );

    return {
      isDisabled: disabledReasons.length > 0,
      reasons: disabledReasons,
    };
  }, [model, body]);
}

function getIsInputValidAndDefined(
  input:
    | NonNullable<GetModelsByModelIdApiResponse["model"]["inputs"]>[number]
    | undefined,
  value: Body[string]
) {
  if (!input) {
    return false;
  }

  if (input.type === "file") {
    return !!value;
  } else if (input.type.includes("array")) {
    const isInvalid =
      !value ||
      !Array.isArray(value) ||
      value.length === 0 ||
      (input.minLength !== undefined && value.length < input.minLength) ||
      (input.maxLength !== undefined && value.length > input.maxLength);
    if (isInvalid) {
      return false;
    } else if (input.type === "string_array" || input.type === "file_array") {
      return value.every((v) => typeof v === "string");
    } else if (input.type === "number_array") {
      return value.every((v) => typeof v === "number");
    }
  } else if (input.type === "string") {
    return (
      !!value &&
      typeof value === "string" &&
      (input.minLength === undefined || value.length >= input.minLength) &&
      (input.maxLength === undefined || value.length <= input.maxLength)
    );
  } else if (input.type === "number") {
    return (
      value !== undefined &&
      typeof value === "number" &&
      (input.min === undefined || value >= input.min) &&
      (input.max === undefined || value <= input.max)
    );
  } else if (input.type === "boolean") {
    return value !== undefined;
  }
  return !!value;
}
